import parse from 'html-react-parser';
import supportedCurrencies from '../../i18n/supported-currencies_dict.json';
// Leaf import rather than the constants barrel — helpers are pulled in early and the barrel closes
// a cycle that only fails at runtime (see CLAUDE.md).
import {AMOUNT_TYPE, DEFAULT_CURRENCY_CODE} from '../../constants/amount.constants';
import {localizationHelpers} from './localization.helpers';
import {objectHelpers} from './object.helpers';
import {textHelpers} from './text.helpers';


export const amountHelpers = {
  /**
   * Resolves which amount comparator a list filter should show, given the bounds its API query
   * carries.
   *
   * The list filters sync their local state from the query the list is currently loaded with, and
   * that query gets a fresh object identity after every request — so the sync runs again on changes
   * the filter itself caused. While no amount has been entered the query carries neither bound and
   * therefore cannot encode the comparator at all, and deriving one anyway snapped the user's
   * choice back to MAX: picking "at least" before typing an amount was silently thrown away
   * (H1MVP-4614 / H1MVP-4615).
   *
   * Each list names the bounds differently — `low_value`/`high_value` on expenses,
   * `amount_gte`/`amount_lte` on documents — so the caller passes the values, not the keys.
   *
   * @param {Object} params
   * @param {number|string|null|undefined} params.lowerBound - The query's "at least" bound, if any.
   * @param {number|string|null|undefined} params.upperBound - The query's "at most" bound, if any.
   * @param {string} params.fallback - Comparator to keep when the query carries neither bound.
   * @returns {string} An AMOUNT_TYPE value.
   * @example
   * amountHelpers.resolveAmountType({lowerBound: 20, upperBound: null, fallback: AMOUNT_TYPE.MAX}); // MIN
   * amountHelpers.resolveAmountType({lowerBound: null, upperBound: null, fallback: AMOUNT_TYPE.MIN}); // MIN
   */
  resolveAmountType: ({lowerBound, upperBound, fallback}) => {
    if (lowerBound == null && upperBound == null) return fallback;
    return lowerBound != null ? AMOUNT_TYPE.MIN : AMOUNT_TYPE.MAX;
  },
  /**
   * Normalizes a price value to a finite number, handling string inputs with comma decimal separators.
   * @param {number|string|null|undefined} value - The price value to normalize.
   * @param {number|string} [compareWith] - When provided, returns null if the normalized value equals this (string-coerced comparison).
   * @returns {number|null} The normalized numeric value, or null if the value is absent, unparseable, or equal to compareWith.
   * @example
   * amountHelpers.normalizePrice('94,2');          // 94.2
   * amountHelpers.normalizePrice('94,2', 94.2);    // null (unchanged)
   * amountHelpers.normalizePrice(null);             // null
   * amountHelpers.normalizePrice('abc');            // null
   */
  normalizePrice: (value, compareWith) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') {
      const parsed = Number(String(value).replace(/,/g, '.'));
      if (!Number.isFinite(parsed)) return null;
      value = parsed;
    }
    if (compareWith !== undefined && String(value) === String(compareWith)) return null;
    return value;
  },
  /**
   * Formats a numeric value into a locale-specific string using the given separator ID.
   * @param {number} value - Numeric amount to format.
   * @param {string} separatorId - Separator identifier (e.g. 'SID02', 'SID03', 'SID04'; defaults to SID01).
   * @param {boolean} [showFractionalPart=true] - When false, hides the fractional part if it is '00'.
   * @returns {string} Formatted amount string with the appropriate thousands and decimal separators.
   * @example
   * amountHelpers.getAmountBasedOnSeparator(1234.5, 'SID02'); // '1,234.50'
   */
  getAmountBasedOnSeparator: (value, separatorId, showFractionalPart = true) => {
    const parts = Number(value).toFixed(2).toString().split('.');
    let str, separator;
    const integerPart = parts[0];
    const fractionalPart = parts[1];
    switch(separatorId) {
      case 'SID02':
        str = textHelpers.separateString(integerPart, ',');
        separator = '.';
        break;
      case 'SID03':
        str = textHelpers.separateString(integerPart, ' ');
        separator = '.';
        break;
      case 'SID04':
        str = textHelpers.separateString(integerPart, '.');
        separator = ',';
        break;
      case 'SID01':
      default:
        str = textHelpers.separateString(integerPart, ' ');
        separator = ',';
        break;
    }

    return !showFractionalPart && fractionalPart === "00" ? str : `${str}${separator}${fractionalPart}`;
  },

  /**
   * Formats a numeric value as an abbreviated euro amount (e.g. K, M, B).
   * @param {number} value - Numeric amount to abbreviate.
   * @returns {string} Abbreviated currency string prefixed with '€'.
   * @example
   * amountHelpers.formatCurrencyAbbreviated(1500000); // '€1.50M'
   */
  formatCurrencyAbbreviated: (value) => {
    if (value >= 1e9) return `€${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `€${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `€${(value / 1e3).toFixed(2)}K`;
    if (value > 0) value = value.toFixed(2);
    return `€${value.toString()}`;
  },

  /**
   * Returns a formatted React element or string displaying an amount with its currency symbol.
   * @param {number} value - Numeric amount to display.
   * @param {Object} [options={}] - Formatting options.
   * @param {string|undefined} [options.countryCode=undefined] - ISO 3166-1 alpha-2 country code used to resolve currency when currencyCode is absent.
   * @param {string|undefined} [options.currencyCode=undefined] - ISO 4217 currency code (takes precedence over countryCode).
   * @param {boolean} [options.showCurrencyCode=true] - When false, returns only the formatted number string.
   * @param {boolean} [options.showFractionalPart=true] - Passed to getAmountBasedOnSeparator to control fractional display.
   * @returns {React.ReactElement|string} Formatted amount with currency symbol or plain number string.
   * @example
   * amountHelpers.getAmountWithCurrencyCode(1500, {currencyCode: 'EUR'}); // <>'€1 500,00'</>
   */
  getAmountWithCurrencyCode: (
    value,
    {countryCode, currencyCode, showCurrencyCode = true, showFractionalPart = true} = {}
  ) => {
    let curCode = currencyCode;
    let separatorId;

    if (!curCode || objectHelpers.isNaV(curCode)) {
      const country = countryCode
        ? localizationHelpers.getCountryByCode(countryCode)
        : null;

      curCode = country?.currency_alphabetic_code || DEFAULT_CURRENCY_CODE;
      separatorId = country?.separator_id;
    }

    const currency = amountHelpers.getCurrencyCode(curCode);
    const symbol = parse(currency.symbol);
    const price = amountHelpers.getAmountBasedOnSeparator(
      Math.abs(value),
      separatorId,
      showFractionalPart
    );

    if (!showCurrencyCode) return price;

    const sign = value < 0 ? '-' : '';

    return currency.position === 'after'
      ? `${sign}${price}${symbol}`
      : `${sign}${symbol}${price}`;
  },

  /**
   * Looks up a currency object from the supported currencies dictionary by code, symbol, name, or official name.
   * @param {string} input - Currency code, symbol, name, or official name to look up.
   * @returns {{currency: string, symbol: string, position?: string, name?: string, OfficialName?: string}} Currency object or fallback with the raw input as both currency and symbol.
   * @example
   * amountHelpers.getCurrencyCode('USD'); // { currency: 'USD', symbol: '$', position: 'before', ... }
   */
  getCurrencyCode: (input) => {
    const unknownValue = {currency: input, symbol: input};
    if (!input) return unknownValue;

    const normalizedInput = input.toString().trim().toLowerCase();

    // First: exact match on AlphabeticCode
    for (const code in supportedCurrencies) {
      if (code.toLowerCase() === normalizedInput) {
        return supportedCurrencies[code];
      }
    }

    // Second: exact match on symbol, name, or official name
    for (const code in supportedCurrencies) {
      const currency = supportedCurrencies[code];
      if (
        currency.symbol.toLowerCase() === normalizedInput ||
        currency.name.toLowerCase() === normalizedInput ||
        currency.OfficialName.toLowerCase() === normalizedInput
      ) {
        return currency;
      }
    }

    // Optional: handle prefixed/suffixed symbol (like US$, CAD$, etc.)
    for (const code in supportedCurrencies) {
      const currency = supportedCurrencies[code];
      if (
        normalizedInput.endsWith(currency.symbol.toLowerCase()) &&
        normalizedInput.length > currency.symbol.length
      ) {
        return currency;
      }
    }

    return unknownValue;
  },

  /**
   * Returns the display symbol for a currency, for places that need only the symbol (e.g. an input prefix).
   * @param {string} [currencyCode] - Currency code, symbol, name, or official name. Falls back to EUR when empty.
   * @returns {string|React.ReactNode} The HTML-entity-parsed symbol, `€` when no currency is given.
   * @example
   * amountHelpers.getCurrencySymbol('USD'); // '$'
   * amountHelpers.getCurrencySymbol(undefined); // '€'
   */
  getCurrencySymbol: (currencyCode) => {
    if (!currencyCode || objectHelpers.isNaV(currencyCode)) return '€';

    return parse(amountHelpers.getCurrencyCode(currencyCode).symbol);
  }
}
